import { FormEvent, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ExtractScanOutput,
  Profile,
  ScanReportType,
} from '@/types/db';
import type { UploadedAttachment } from '@/features/timeline/lib/upload';
import { codeToFlag } from '@/features/timeline/lib/aiFlags';
import { FieldFlagsGroup } from '@/features/timeline/components/flag-chip';
import { saveExtraction } from '@/features/timeline/lib/save';
import { ReviewChrome } from './review-chrome';

interface Props {
  profile: Profile;
  eventId: string;
  attachment: UploadedAttachment;
  extraction: ExtractScanOutput;
}

interface FormState {
  eventDate: string;
  doctorName: string;
  clinicName: string;
  clinicPhone: string;
  clinicAddress: string;
  insuranceCompany: string;
  insuranceClaim: string;
  reportConclusion: string;
  notes: string;
}

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

const REPORT_TYPE_LABEL: Record<ScanReportType, string> = {
  x_ray: 'X-ray',
  ct: 'CT',
  mri: 'MRI',
  ultrasound: 'Ultrasound',
  mammography: 'Mammography',
  pet: 'PET',
  other: 'Imaging',
};

function initFormState(e: ExtractScanOutput): FormState {
  return {
    eventDate: e.event_date ?? '',
    doctorName: e.doctor_name ?? '',
    clinicName: e.clinic_name ?? '',
    clinicPhone: e.clinic_phone ?? '',
    clinicAddress: e.clinic_address ?? '',
    insuranceCompany: e.insurance?.company ?? '',
    insuranceClaim: e.insurance?.claim_number ?? '',
    reportConclusion: e.diagnosis ?? '',
    notes: e.notes ?? '',
  };
}

function nullIfEmpty(v: string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
}

export function ScanReview({ profile, eventId, attachment, extraction }: Props) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => initFormState(extraction));
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const aiFlags = useMemo(
    () => (extraction.flags ?? []).map((c) => codeToFlag(c)),
    [extraction.flags],
  );

  const reportType = extraction.report_data?.report_type;
  const regions = useMemo(() => {
    const r = extraction.report_data;
    if (r?.regions && r.regions.length > 0) return r.regions;
    if (r?.region) return [r.region];
    return [];
  }, [extraction.report_data]);
  const radiologist = extraction.report_data?.radiologist_name ?? null;
  const requestingDoctor = extraction.report_data?.requesting_doctor ?? null;
  const clinicalHistory = extraction.report_data?.clinical_history ?? null;
  const technique = extraction.report_data?.technique ?? null;
  const contrastUsed = extraction.report_data?.contrast_used;
  const findings = extraction.report_data?.findings ?? null;

  async function handleSave(e?: FormEvent) {
    e?.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaveError(null);
    setSubmitting(true);
    try {
      await saveExtraction({
        profileId: profile.id,
        eventId,
        eventType: 'scan',
        eventDate: nullIfEmpty(form.eventDate),
        doctorName: nullIfEmpty(form.doctorName),
        doctorSpecialty: null,
        clinicName: nullIfEmpty(form.clinicName),
        clinicPhone: nullIfEmpty(form.clinicPhone),
        clinicAddress: nullIfEmpty(form.clinicAddress),
        insuranceCompany: nullIfEmpty(form.insuranceCompany),
        insuranceClaim: nullIfEmpty(form.insuranceClaim),
        diagnosis: nullIfEmpty(form.reportConclusion),
        notes: nullIfEmpty(form.notes),
        title: extraction.title,
        aiFlags,
        medications: [],
        attachments: [attachment],
      });
      navigate(`/profile/${profile.id}`, {
        replace: true,
        state: { justSavedEventId: eventId },
      });
    } catch (err) {
      console.error('[review:scan] save failed', err);
      setSaveError("Couldn't save this record. Please try again.");
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  return (
    <ReviewChrome
      profile={profile}
      attachment={attachment}
      headerTitle="Review scan report"
      photoCaption="Open the report to cross-check the editable fields below."
      photoAlt="Scan report"
      cancelMessage="Discard this scan report? The photo and any edits will be lost."
      onSubmit={() => handleSave()}
      submitting={submitting}
      saveError={saveError}
    >
      <div className="rounded-lg border border-accent/30 bg-accent-soft p-3 mb-4">
        <p className="text-sm text-ink mb-1">
          <span className="font-semibold">We read this scan report.</span>{' '}
          The radiologist's findings below are read-only — edit basics on the right.
        </p>
        <p className="text-xs text-ink-muted">
          Caremory does not interpret images. Discuss any finding with a clinician.
        </p>
      </div>

      {aiFlags.length > 0 && (
        <div className="mb-4">
          <FieldFlagsGroup flags={aiFlags} />
        </div>
      )}

      {/* Read-only report metadata + findings */}
      <div className="card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-ink">
            Report
            <span className="ml-2 text-xs text-ink-muted font-normal">
              read-only
            </span>
          </h2>
          {reportType && (
            <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-evt-scan-soft text-evt-scan-ink border border-border">
              {REPORT_TYPE_LABEL[reportType]}
            </span>
          )}
        </div>

        {regions.length > 0 && (
          <div className="mb-3">
            <p className="text-xs uppercase tracking-wide text-ink-muted mb-1">
              Region{regions.length > 1 ? 's' : ''}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {regions.map((r, i) => (
                <span
                  key={`${r}-${i}`}
                  className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-canvas border border-border text-ink"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}

        <dl className="grid grid-cols-1 gap-y-2 text-sm">
          {radiologist && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-ink-muted">
                Radiologist
              </dt>
              <dd className="text-ink">{radiologist}</dd>
            </div>
          )}
          {requestingDoctor && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-ink-muted">
                Requesting doctor
              </dt>
              <dd className="text-ink">{requestingDoctor}</dd>
            </div>
          )}
          {clinicalHistory && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-ink-muted">
                Clinical history
              </dt>
              <dd className="text-ink whitespace-pre-line">{clinicalHistory}</dd>
            </div>
          )}
          {technique && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-ink-muted">
                Technique
              </dt>
              <dd className="text-ink">
                {technique}
                {typeof contrastUsed === 'boolean' && (
                  <span className="ml-2 text-xs text-ink-muted">
                    {contrastUsed ? '(with contrast)' : '(no contrast)'}
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>

        {findings ? (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs uppercase tracking-wide text-ink-muted mb-1">
              Findings
            </p>
            <p className="text-sm text-ink whitespace-pre-line">{findings}</p>
          </div>
        ) : (
          <p className="mt-3 pt-3 border-t border-border text-sm text-ink-muted">
            No verbatim findings were captured. Open the report to read the original.
          </p>
        )}
      </div>

      {/* Editable basics */}
      <form onSubmit={handleSave} className="card p-5 space-y-4">
        <h2 className="text-sm font-bold text-ink">Basics</h2>

        <div>
          <label htmlFor="eventDate" className="label">Date</label>
          <input
            id="eventDate"
            type="date"
            value={form.eventDate}
            max={TODAY_ISO()}
            onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="doctorName" className="label">Reporting doctor</label>
          <input
            id="doctorName"
            type="text"
            value={form.doctorName}
            onChange={(e) => setForm({ ...form, doctorName: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="clinicName" className="label">Imaging center</label>
          <input
            id="clinicName"
            type="text"
            value={form.clinicName}
            onChange={(e) => setForm({ ...form, clinicName: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="clinicPhone" className="label">Phone</label>
          <input
            id="clinicPhone"
            type="tel"
            value={form.clinicPhone}
            placeholder="e.g. +20 2 1234 5678"
            onChange={(e) => setForm({ ...form, clinicPhone: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="clinicAddress" className="label">Address</label>
          <input
            id="clinicAddress"
            type="text"
            value={form.clinicAddress}
            onChange={(e) =>
              setForm({ ...form, clinicAddress: e.target.value })
            }
            className="input"
          />
        </div>

        <div>
          <label htmlFor="reportConclusion" className="label">
            Report conclusion
          </label>
          <textarea
            id="reportConclusion"
            value={form.reportConclusion}
            onChange={(e) =>
              setForm({ ...form, reportConclusion: e.target.value })
            }
            placeholder="Radiologist's impression / conclusion verbatim, if present."
            className="input py-2 min-h-[72px]"
            rows={3}
          />
        </div>

        <div>
          <label htmlFor="notes" className="label">Notes</label>
          <textarea
            id="notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Auto-filled with any urgent / critical-keyword sentence from the report."
            className="input py-2 min-h-[72px]"
            rows={3}
          />
        </div>

        <details className="rounded-lg border border-border bg-canvas">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink-muted">
            Insurance (optional)
          </summary>
          <div className="px-3 pb-3 pt-1 space-y-3">
            <div>
              <label htmlFor="insuranceCompany" className="label">
                Insurance company
              </label>
              <input
                id="insuranceCompany"
                type="text"
                value={form.insuranceCompany}
                onChange={(e) =>
                  setForm({ ...form, insuranceCompany: e.target.value })
                }
                className="input"
              />
            </div>
            <div>
              <label htmlFor="insuranceClaim" className="label">
                Claim / policy #
              </label>
              <input
                id="insuranceClaim"
                type="text"
                value={form.insuranceClaim}
                onChange={(e) =>
                  setForm({ ...form, insuranceClaim: e.target.value })
                }
                className="input"
              />
            </div>
          </div>
        </details>
      </form>
    </ReviewChrome>
  );
}
